import { createContext, createElement, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { GoogleAuthProvider, getAuth, onAuthStateChanged, signInWithPopup, signOut as firebaseSignOut, type User } from 'firebase/auth'
import { getApps, initializeApp, type FirebaseApp } from 'firebase/app'
import type { AuthUser } from './types'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  // Safari partitions storage for cross-site auth helpers. On the production
  // Pages domain we proxy Firebase's auth helper under /__/auth, so keeping the
  // helper on the same origin preserves the redirect/popup state on iOS.
  authDomain: typeof window !== 'undefined' && window.location.hostname === 'surg-practical-medical-horizon.pages.dev'
    ? window.location.hostname
    : import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

export const firebaseEnabled = [firebaseConfig.apiKey, firebaseConfig.authDomain, firebaseConfig.projectId, firebaseConfig.appId]
  .every((value) => typeof value === 'string' && value.trim().length > 0)

let firebaseApp: FirebaseApp | undefined
let auth: ReturnType<typeof getAuth> | undefined
if (firebaseEnabled) {
  firebaseApp = getApps()[0] ?? initializeApp(firebaseConfig)
  auth = getAuth(firebaseApp)
}

const provider = new GoogleAuthProvider()
provider.setCustomParameters({ prompt: 'select_account' })

export const demoUser: AuthUser = {
  uid: 'demo-user', displayName: 'طالب تجريبي', email: 'demo@medical-horizon.local', photoURL: null, isDemo: true,
}

function mapFirebaseUser(user: User | null): AuthUser | null {
  return user ? { uid: user.uid, displayName: user.displayName, email: user.email, photoURL: user.photoURL } : null
}

export async function signInWithGoogle(): Promise<AuthUser> {
  if (!firebaseEnabled || !auth) return demoUser
  const result = await signInWithPopup(auth, provider)
  const user = mapFirebaseUser(result.user)
  if (!user) throw new Error('تعذر إنشاء جلسة تسجيل الدخول.')
  return user
}

export async function signOut(): Promise<void> {
  if (auth) await firebaseSignOut(auth)
}

export async function getFirebaseIdToken(): Promise<string | null> {
  if (!auth?.currentUser) return null
  return auth.currentUser.getIdToken()
}
export const getAuthToken = getFirebaseIdToken
export const getIdToken = getFirebaseIdToken

export interface AuthSession {
  user: AuthUser | null
  loading: boolean
  configured: boolean
  isDemo: boolean
  isAdmin: boolean
  accountError: string | null
  signIn: () => Promise<AuthUser>
  signOut: () => Promise<void>
}

const AuthSessionContext = createContext<AuthSession | null>(null)

async function loadServerProfile(): Promise<{ isAdmin: boolean }> {
  const token = await getFirebaseIdToken()
  const response = await fetch('/api/me', {
    headers: token ? { Authorization: `Bearer ${token}`, accept: 'application/json' } : { accept: 'application/json' },
  })
  const payload = await response.json().catch(() => ({})) as { user?: { isAdmin?: boolean }; error?: string; message?: string }
  if (!response.ok) {
    if (response.status === 403 && payload.error === 'account_blocked') throw new Error('تم إيقاف هذا الحساب من إدارة المنصة.')
    throw new Error(payload.message || 'تعذر التحقق من جلسة الدخول.')
  }
  return { isAdmin: payload.user?.isAdmin === true }
}

export function AuthSessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(firebaseEnabled ? null : demoUser)
  const [loading, setLoading] = useState(firebaseEnabled)
  const [isAdmin, setIsAdmin] = useState(false)
  const [accountError, setAccountError] = useState<string | null>(null)

  useEffect(() => {
    if (!firebaseEnabled || !auth) {
      setLoading(false)
      return undefined
    }
    return onAuthStateChanged(auth, async (firebaseUser) => {
      const mapped = mapFirebaseUser(firebaseUser)
      setUser(mapped)
      setIsAdmin(false)
      setAccountError(null)
      if (!mapped) {
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        const profile = await loadServerProfile()
        setIsAdmin(profile.isAdmin)
      } catch (error) {
        setAccountError(error instanceof Error ? error.message : 'تعذر التحقق من الحساب.')
      } finally {
        setLoading(false)
      }
    })
  }, [])

  const value = useMemo<AuthSession>(() => ({
    user, loading, configured: firebaseEnabled, isDemo: !firebaseEnabled, isAdmin, accountError,
    signIn: signInWithGoogle, signOut,
  }), [accountError, isAdmin, loading, user])
  return createElement(AuthSessionContext.Provider, { value }, children)
}

export function useAuthSession(): AuthSession {
  const session = useContext(AuthSessionContext)
  if (!session) throw new Error('useAuthSession must be used inside AuthSessionProvider')
  return session
}
